import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { AnimalSelectionService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { LoaderService, ToastService } from '@tcc-mono/shared/ui/services';
import { withLoading } from '@tcc-mono/shared/utils';
import {
  AnimalSelection,
  AnimalSelectionSource,
  Stable,
  animalSelectionUsesRvoPool,
  formatAgeRangeHints,
  getSelectionMarkColorHex,
  isValidBirthDateRange,
  resolveBirthDateReference,
  SELECTION_MARK_COLORS,
  toDateInputValue,
} from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { StableSelectorComponent } from '@tcc-mono/vandijk/shared/ui/entity-selectors/stable-selector';
import { Observable, filter, from, map, switchMap, take, tap } from 'rxjs';

@Component({
  templateUrl: './animal-selection-dialog.component.html',
  styleUrls: ['./animal-selection-dialog.component.scss']
})
export class AnimalSelectionDialogComponent implements OnInit {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _animalSelectionService: AnimalSelectionService = inject(AnimalSelectionService);
  private readonly _loading: LoaderService = inject(LoaderService);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _toast: ToastService = inject(ToastService);

  @Input('selection') public selection: AnimalSelection;

  public form: FormGroup;
  public readonly filterColorNames = SELECTION_MARK_COLORS;
  public readonly sourceOptions: { value: AnimalSelectionSource; label: string }[] = [
    { value: 'rvo', label: 'RVO-stal' },
    { value: 'excel', label: 'Excel-pool' }
  ];

  public ngOnInit(): void {
    this.form = this._initForm();
    if (this.selection?.min_birth_date || this.selection?.max_birth_date) {
      this.form.patchValue({
        min_birth_date: toDateInputValue(this.selection.min_birth_date),
        max_birth_date: toDateInputValue(this.selection.max_birth_date),
      });
    }
    if (this.selection?.id) {
      this._repo.getSelectionMarkColorsForSelection(this.selection.id).pipe(take(1)).subscribe(colors => {
        const fa = this.form.get('filter_colors') as FormArray;
        colors.forEach((c, i) => {
          if (fa.at(i)) {
            fa.at(i).patchValue({
              used: c.used !== false,
              min_birth_date: toDateInputValue(c.min_birth_date),
              max_birth_date: toDateInputValue(c.max_birth_date),
            });
          }
        });
      });
    }
  }

  get filterColors(): FormArray {
    return this.form.get('filter_colors') as FormArray;
  }

  public getFilterColorName(index: number): string {
    return this.filterColorNames[index]?.name ?? '';
  }

  public getMarkColorHex(value: string): string {
    return getSelectionMarkColorHex(value);
  }

  public ageRangeHints(minBirthDate?: string | null, maxBirthDate?: string | null): string[] {
    return formatAgeRangeHints(
      minBirthDate,
      maxBirthDate,
      resolveBirthDateReference(this.selection?.created_at),
    );
  }

  public editStable(type: 'source' | 'destination'): void {
    from(
      this._modalCtrl.create({
        component: StableSelectorComponent,
        componentProps: {
          params: {
            has_rvo: true
          }
        }
      })
    )
      .pipe(
        tap(modal => modal.present()),
        switchMap(modal => modal.onDidDismiss<Stable>()),
        map(({ data }) => data),
        filter(stable => !!stable),
        tap((stable: Stable) => {
          this.form.get(type + '_stable_id').setValue(stable.id);
          this.form.get(type + '_stable_name').setValue(stable.name);
          this.form
            .get(type + '_stable_relation_name')
            .setValue(stable.relation_name);
        })
      )
      .subscribe();
  }

  public getStableName(type: 'source' | 'destination'): string {
    return this.form?.get(type + '_stable_name')?.value
      ? `${this.form.get(type + '_stable_name').value} (${this.form.get(type + '_stable_relation_name').value
      })`
      : '-';
  }

  public downloadExcelTemplate(): void {
    this._animalSelectionService
      .getExcelAnimalsTemplate()
      .pipe(take(1))
      .subscribe((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'dierlijst_template.xlsx';
        link.click();
        URL.revokeObjectURL(url);
      });
  }

  public onExcelFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !this.selection?.id) return;
    this._animalSelectionService
      .uploadExcelAnimals(this.selection.id, file)
      .pipe(withLoading(this._loading), take(1))
      .subscribe({
        next: () => {
          this._toast.show({
            header: 'Excel geüpload',
            message: 'De bestaande dierlijst is vervangen door de nieuwe.',
            color: 'success'
          });
          input.value = '';
        },
        error: () => {
          input.value = '';
        }
      });
  }

  public onSourceChange(): void {
    if (this.selection?.id == null) {
      return;
    }
    this.save(false);
  }

  public save(close: boolean = true): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this._validateBirthDateRanges()) {
      this._toast.show({
        header: 'Ongeldige datum',
        message: 'Controleer de geboortedatum-ranges (van mag niet na tot; actieve kleuren vereisen minimaal één grens).',
        color: 'danger',
      });
      return;
    }

    const { filter_colors: _, source_stable_name, source_stable_relation_name, ...selectionFields } = this.form.value;
    const selectionToSave: AnimalSelection = {
      ...this.selection,
      remark: selectionFields.remark,
      source_stable_id: selectionFields.source_stable_id,
      min_birth_date: selectionFields.min_birth_date || null,
      max_birth_date: selectionFields.max_birth_date || null,
      source: selectionFields.source,
    };

    const call$: Observable<AnimalSelection> = selectionToSave.id
      ? this._repo.updateEntity(selectionToSave.id, selectionToSave)
      : this._repo.addEntity(selectionToSave);

    call$.pipe(
      withLoading(this._loading),
      take(1),
      switchMap((selection: AnimalSelection) => {
        const body = {
          selection_mark_colors: this.filterColors.controls.map((ctrl) => ({
            color_name: ctrl.get('color_name')?.value,
            used: ctrl.get('used')?.value ?? true,
            min_birth_date: ctrl.get('used')?.value ? (ctrl.get('min_birth_date')?.value || null) : null,
            max_birth_date: ctrl.get('used')?.value ? (ctrl.get('max_birth_date')?.value || null) : null,
          }))
        };
        return this._repo.updateSelectionMarkColors(selection.id, body).pipe(
          take(1),
          map(() => selection)
        );
      }),
      take(1)
    ).subscribe({
      next: (selection: AnimalSelection) => {
        if (close) {
          this._modalCtrl.dismiss(selection);
        }
      }
    });
  }

  private _validateBirthDateRanges(): boolean {
    const min = this.form.get('min_birth_date')?.value;
    const max = this.form.get('max_birth_date')?.value;
    if (!isValidBirthDateRange(min, max)) {
      return false;
    }

    for (const ctrl of this.filterColors.controls) {
      if (!ctrl.get('used')?.value) {
        continue;
      }
      const min = ctrl.get('min_birth_date')?.value;
      const max = ctrl.get('max_birth_date')?.value;
      if (!min && !max) {
        return false;
      }
      if (!isValidBirthDateRange(min, max)) {
        return false;
      }
    }

    return true;
  }

  private _initForm(): FormGroup {
    const initialSource: AnimalSelectionSource = this.selection?.source
      ?? (animalSelectionUsesRvoPool(this.selection ?? {}) ? 'rvo' : 'excel');

    return this._fb.group({
      remark: [this.selection?.remark ?? this.selection?.name ?? ''],
      source_stable_id: [this.selection?.source_stable_id, [Validators.required]],
      source_stable_name: [this.selection?.source_stable_name],
      source_stable_relation_name: [this.selection?.source_stable_relation_name],
      min_birth_date: [null as string | null],
      max_birth_date: [null as string | null],
      source: [initialSource],
      filter_colors: this._fb.array(
        SELECTION_MARK_COLORS.map((c) =>
          this._fb.group({
            color_name: [c.value],
            used: [false],
            min_birth_date: [null as string | null],
            max_birth_date: [null as string | null],
          })
        )
      )
    });
  }
}
